import { motion } from "framer-motion";
import { Target, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";

const Goals = () => {
  const objectives = [
    {
      title: "Modernisation de l'Artisanat",
      description: "Le PMN vise la modernisation du secteur de l'Artisanat National",
      icon: <Target className="w-8 h-8" />,
    },
    {
      title: "Accompagnement des Artisans",
      description:
        "Accompagner les artisans pour leur permettre de mieux répondre aux exigences de la commande publique, par la formation, le perfectionnement, le financement et l'accès au foncier",
      icon: <CheckCircle2 className="w-8 h-8" />,
    },
    {
      title: "Suivi de la Commande Publique",
      description:
        "Coordonner et de suivre l'évolution de l'accès des artisans à la commande publique",
      icon: <CheckCircle2 className="w-8 h-8" />,
    },
    {
      title: "Valorisation du Génie Créateur",
      description:
        "Confirmer l'investissement du génie créateur sénégalais dans le développement économique du pays",
      icon: <CheckCircle2 className="w-8 h-8" />,
    },
    {
      title: "Reproduction des Prototypes",
      description:
        "Vulgariser le projet du mobilier national par la reproduction des prototypes déjà existants",
      icon: <CheckCircle2 className="w-8 h-8" />,
    },
    {
      title: "Innovation et Création",
      description:
        "Créer de nouveaux prototypes par l'organisation d'un Concours du Mobilier national",
      icon: <CheckCircle2 className="w-8 h-8" />,
    },
  ];

  return (
    <>
      <SEO
        title="Objectifs"
        description="Explorez les objectifs du Projet Mobilier National : modernisation de l'artisanat, accompagnement des artisans, valorisation du patrimoine artisanal sénégalais."
        keywords="PMN, Objectifs, Artisanat, Modernisation, Accompagnement, Patrimoine, Artisans, Sénégal"
        ogImage="/images/side-image-obj.jpeg"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-[#1E3D1A] py-32">
          <div className="absolute inset-0 bg-[url('/objectif-pmn.webp')] opacity-60" />
          <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-wider">
                OBJECTIFS DU PMN
              </h1>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* First Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto mb-20">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl"
              >
                <img
                  src="/images/side-image-obj.jpeg"
                  alt="Objectifs PMN"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <p className="text-lg text-gray-700 leading-relaxed">
                  La mise en oeuvre dans les meilleurs délais du PMN est sous-tendue par des objectifs clairement définis.
                </p>
                
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="bg-[#1E3D1A] p-3 rounded-lg text-white">
                      {objectives[0].icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1E3D1A] mb-2">
                        {objectives[0].title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {objectives[0].description}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="bg-[#1E3D1A] p-3 rounded-lg text-white">
                      {objectives[1].icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#1E3D1A] mb-2">
                        {objectives[1].title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {objectives[1].description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="space-y-6"
              >
                {objectives.slice(2).map((objective, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 shadow-lg"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#1E3D1A] p-3 rounded-lg text-white">
                        {objective.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#1E3D1A] mb-2">
                          {objective.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {objective.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl"
              >
                <img
                  src="/images/side-image-obj-1.jpeg"
                  alt="Impact PMN"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Goals;
